import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Skeleton } from '@heroui/react';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownSection,
  DropdownItem,
} from '@/shared/ui';
import { NotificationIcon } from '@/shared/ui/icons/NotificationIcon';
import { useUnreadNotifications } from '@/entities/notification';
import {
  useNotificationActions,
  NotificationItem,
} from '@/features/notification-actions';
import { ROUTES } from '@/shared/config';

export const NotificationsDropdown: React.FC = () => {
  const navigate = useNavigate();
  const { data: unreadData, isLoading } = useUnreadNotifications({
    page: 0,
    size: 5,
  });
  const { markAsRead, markAllAsRead } = useNotificationActions();

  const notifications = unreadData?.data?.content || [];
  const unreadCount = unreadData?.data?.total_elements || 0;

  return (
    <Dropdown placement="bottom-end">
      <DropdownTrigger>
        <Button
          isIconOnly
          aria-label={`${unreadCount}개의 읽지 않은 알림`}
          radius="full"
          variant="light"
          className="relative overflow-visible"
        >
          <NotificationIcon size={24} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-5 h-5 px-1 text-xs font-semibold text-white bg-danger rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="알림"
        variant="flat"
        classNames={{
          base: 'w-80 max-w-[calc(100vw-2rem)] bg-background dark:border-default-100',
          list: 'py-1 px-1',
        }}
      >
        <DropdownSection
          title="알림"
          showDivider
          classNames={{
            heading: 'text-tiny text-default-500 font-bold uppercase px-3 py-2',
            group: 'py-0',
            divider: 'mx-3',
          }}
        >
          <DropdownItem
            key="notification-list"
            isReadOnly
            className="p-0 hover:bg-transparent data-[hover=true]:bg-transparent"
            textValue="알림 목록"
          >
            <div
              className="max-h-[320px] sm:max-h-[360px] overflow-y-auto overscroll-contain px-2 py-1 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-default-200 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"
            >
              {isLoading ? (
                <div className="space-y-2 p-2">
                  <Skeleton className="h-16 w-full rounded-lg" />
                  <Skeleton className="h-16 w-full rounded-lg" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-default-400">
                  <NotificationIcon size={48} className="mb-2 opacity-50" />
                  <p className="text-sm">새로운 알림이 없습니다</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div
                      key={notif.id}
                      onClick={() => markAsRead(notif.id)}
                      className="cursor-pointer transition-transform active:scale-[0.98] rounded-xl"
                    >
                      <NotificationItem
                        notification={notif}
                        onMarkAsRead={markAsRead}
                        className="border-0 shadow-none"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DropdownItem>
        </DropdownSection>
        <DropdownSection
          classNames={{
            group: 'py-2',
            divider: 'mx-3',
          }}
        >
          {unreadCount > 0 ? (
            <>
              <DropdownItem
                key="mark-all"
                onPress={markAllAsRead}
                classNames={{
                  base: 'py-2 px-3 rounded-lg text-center data-[hover=true]:bg-default-100',
                  title: 'text-sm font-medium text-default-700',
                }}
              >
                모두 읽음 처리
              </DropdownItem>
              <DropdownItem
                key="view-all"
                onPress={() => navigate(ROUTES.NOTIFICATIONS)}
                classNames={{
                  base: 'py-2 px-3 rounded-lg text-center data-[hover=true]:bg-primary/10',
                  title: 'text-sm font-medium text-primary',
                }}
              >
                모든 알림 보기
              </DropdownItem>
            </>
          ) : (
            <DropdownItem
              key="view-all"
              onPress={() => navigate(ROUTES.NOTIFICATIONS)}
              classNames={{
                base: 'py-2 px-3 rounded-lg text-center data-[hover=true]:bg-primary/10',
                title: 'text-sm font-medium text-primary',
              }}
            >
              모든 알림 보기
            </DropdownItem>
          )}
        </DropdownSection>
      </DropdownMenu>
    </Dropdown>
  );
};
